/**
 * Default-deny route policy for guest ("tour") mode.
 *
 * ProtectedRoute admits guests, so every /app route would otherwise be reachable —
 * including authed pages nobody wrapped (e.g. /app/ai-buddy fires an auth-only POST
 * on mount → broken 401 session for a deep-linked guest; Codex #709). Instead of
 * chasing every current and future route, guests get an ALLOWLIST:
 *   - surfaces the tour actually supports (course/lesson/quiz, progress, empathy hub,
 *     games menu + the client-side PitfallSwipe, tools menu), and
 *   - surfaces with their own tailored guest UX (walls/modals with specific copy).
 * Everything else renders a generic GuestWall. New routes are guest-denied by default.
 */
const GUEST_ALLOWED: RegExp[] = [
  // Tour-usable surfaces
  /^\/app$/,
  /^\/app\/progress$/,
  /^\/app\/empathy$/,
  /^\/app\/tools$/,
  /^\/app\/games(\/|$)/, // menu + PitfallSwipe; Echo Arena gates inside games.tsx
  /^\/app\/lesson\/\d+(\/results)?$/,
  /^\/app\/quiz\/\d+$/,
  // Legacy redirect paths the course UI still navigates to (e.g. the lesson page's
  // "Take Quiz" links `/quiz/:id`) — they live under ProtectedRoute and redirect to
  // the /app/* equivalents, so the wall must not intercept them first (Codex #709).
  /^\/lesson\/\d+(\/results)?$/,
  /^\/quiz\/\d+$/,
  // Surfaces with their own tailored guest wall/gate
  /^\/app\/wallet$/,
  /^\/app\/profile$/,
  /^\/app\/tools\/(giraffe-translator|needs-guesser)$/,
  /^\/app\/empy\//,
  /^\/app\/circles\/cohorts\/create$/,
];

export function isGuestAllowedPath(pathname: string): boolean {
  return GUEST_ALLOWED.some((re) => re.test(pathname));
}
